import { IFileEditRequest } from './../../../../../../shared-interfaces/src/lib/interface/api/ifile-edit-request';
import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from '@medlogic/shared/ui/dialog/ui-dialog-confirm';
import { LogService, IAttachmentHeadersResponse, IFileDeleteRequest, ConfigJsonService, UnsubscribeOnDestroyAdapter, IDocumento, GlobalService } from '@medlogic/shared/shared-interfaces';
import { FileUploadService } from '../../service/file-upload.service';
import { catchError, tap, mergeMap, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { error } from '../../service/error';
import { IFileUploadDialog } from '../../interface/IFileUploadDialog';
import { EnFileUploadMode } from '../../enum/EnFileUploadMode';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'lib-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.css']
})
export class FileUploadDialogComponent extends UnsubscribeOnDestroyAdapter implements OnInit {

  dataSource: MatTableDataSource<IDocumento>;
  displayedColumns = ['id', 'documento', 'tipo', 'tamanho', 'versao', 'acoes'];
  singleFileTypes = [EnFileUploadMode.single, EnFileUploadMode.document];
  fileToUpload: File = null;
  progress = 0;
  isLoading = false;
  fileUploadMode = EnFileUploadMode.list;
  selectedDocument: IDocumento;
  showList: boolean = false;
  fileName: string;
  editingId: string | number | null = null;
  acceptSingleFiles: string = 'image/jpg, image/jpeg, image/png';

  ENFILEUPLOADMODE = EnFileUploadMode;

  get isEmpty(): boolean {
    return this.data?.url?.toUpperCase() === 'FOTO' || this.glb.isNullOrEmpty(this.data?.url);
  }

  constructor(
    private fileUploadSrv: FileUploadService,
    private log: LogService,
    private glb: GlobalService,
    private cnfJson: ConfigJsonService,
    private matDialog: MatDialog,
    public dialogRef: MatDialogRef<FileUploadDialogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: IFileUploadDialog
  ) {
    super();
    if (data?.enFileUploadMode) {
      this.fileUploadMode = data?.enFileUploadMode;
    }
  }

  ngOnInit() {
    try {
      this.subs.sink = this.refresh(this.data, this.isEmpty).subscribe(() => this.isLoading = false);
      this.showList = true;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'ngOnInit', error.message);
    }
  }

  private refresh(data: IFileUploadDialog, isEmpty: boolean): Observable<any> {
    if (!data?.ono) {
      return of(null);
    }

    try {
      this.isLoading = true;
      this.progress = 0;
      this.fileToUpload = null;

      const fillDataSource = () => tap((docs: IDocumento[]) => {
        this.dataSource = docs?.length ? new MatTableDataSource<IDocumento>(docs) : null;
      });

      const filterByVno$ = (vno: number) => map((docHeaders: IAttachmentHeadersResponse) => {
        return docHeaders.docs?.filter((doc: IDocumento) =>
          !vno || !this.isSingleFileType() ? true : this.extractVno(doc) === vno
        );
      });

      const selectIfSingle = (parData: IFileUploadDialog, parIsEmpty: boolean) => tap((docs: IDocumento[]) => {
        if (!parIsEmpty && parData?.url && parData?.enFileUploadMode === EnFileUploadMode.single && docs?.length > 0) {
          this.selectedDocument = docs.find(doc => doc.CaminhoFisico === parData.url.replace(/\%3A/g, ':'));
        }
      });

      // Fluxo principal com tratamento de erro embutido
      return this.fileUploadSrv
        .getDocumentHeaders(data.ono, data.tno)
        .pipe(
          filterByVno$(data.vno),
          fillDataSource(),
          selectIfSingle(data, isEmpty),
          catchError((error) => {
            this.log.Registrar(this.constructor.name, 'refresh', `Error in getDocumentHeaders: ${error.message}`);
            return of(null);
          })
        );
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'refresh', `Error in try-catch: ${error.message}`);
      return of(null);
    }
  }

  private extractVno(data: IDocumento): number {
    try {
      // Padrão de nome de arquivo: `f-${ano}--${ono}--${uno}--${vno}--${filename}`
      const regex = /f-\d*--\d*--\d*--(\d*)--.*/;
      const match = regex.exec(data.DocumentoNome);

      if (match && match[1]) {
        const vno = parseInt(match[1], 10);
        return isNaN(vno) ? null : vno;
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'extractVno', error.message);
    }
    return null;
  }

  extractIconName(data: IDocumento): string {
    try {
      const extension = data.Extensao?.toLowerCase();
      const iconMap: { [key: string]: string } = {
        '.jpg': 'panorama',
        '.png': 'panorama',
        '.gif': 'panorama',
        '.jpeg': 'panorama',
        '.bmp': 'panorama',
        '.tif': 'panorama',
        '.doc': 'description',
        '.docx': 'description',
        '.xls': 'table_rows',
        '.xlsx': 'table_rows',
        '.ppt': 'perm_media',
        '.pptx': 'perm_media',
        '.pdf': 'picture_as_pdf'
      };

      return iconMap[extension] || 'insert_drive_file';
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'extractIconName', error.message);
    }
    return null;
  }


  /* Click em fechar. */
  onCloseClick(event?: any): void {
    try {
      if (!this.dataSource || this.dataSource?.data?.length <= 0) {
        this.dialogRef.close({ CaminhoFisico: '' } as IDocumento);
      } else if (this.showList) {
        this.dialogRef.close(false);
      } else {
        this.onPreviewBack(event);
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onCloseClick', error.message);
    }
  }

  onDownload(doc: IDocumento): void {
    try {
      const a = document?.createElement('a');
      a.style.display = 'none';
      a.href = doc?.CaminhoFisico;
      a.download = doc?.DocumentoNomeExibicao;
      document?.body?.appendChild(a);
      a.click();
      document?.body?.removeChild(a);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onDownload', error.message);
    }
  }

  onDelete(doc: IDocumento): void {
    try {
      const width = '60vw';
      const height = '60vh';
      const title = 'Apagar arquivo';
      const text = `Tem certeza que deseja apagar o arquivo: ${doc?.DocumentoNomeExibicao}`;
      const dialog = this.matDialog.open(DialogConfirmComponent, {
        maxWidth: '560px',
        minWidth: '480px',
        maxHeight: '240px',
        width,
        height,
        data: { title, text } });

      this.subs.sink = dialog.afterClosed()
        .pipe(
          this.deleteDocAndRefresh$(doc),
          error()
        ).subscribe((resp) => {
          if(resp) {
            this.isLoading = false;

            this.snackBar.open(`O arquivo ${doc?.DocumentoNomeExibicao} foi removido`, 'Ok', {
             duration: 3000,
             horizontalPosition: 'center',
             verticalPosition: 'top',
             panelClass: ['snackbar-success']
           });
          }
        });
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onDelete', error.message);
    }
  }

  onPreview(doc: IDocumento): void {
    try {
      this.showList = false;

      this.fileUploadMode = EnFileUploadMode.preview;
      this.selectedDocument = doc;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onPreview', error.message);
    }
  }

  canPreview(doc: IDocumento): boolean {
    try {
      if (!doc || this.glb.IsNullOrEmpty(doc?.Extensao)) {
        return false;
      }

      const valid = ['jpg', 'jpeg', 'png', 'gif'];
      return valid.includes(doc?.Extensao?.toLowerCase().replace('.', ''));
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'canPreview', error.message);
    }
  }

  onPreviewBack($event: any): void {
    try {
      this.selectedDocument = null;
      this.showList = true;
      this.fileToUpload = null;
      this.progress = 0;
      this.fileName = null;
      this.editingId = null;
      this.fileUploadMode = EnFileUploadMode.list;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onPreviewBack', error.message);
    }
  }

  private deleteDocAndRefresh$ = (doc: IDocumento) => mergeMap((resp: any) => {
    if (resp.wasConfirmed) {
      this.isLoading = true;
      this.dataSource = null;
      this.onPreviewBack(null);

      return this.fileUploadSrv
        .deleteDocument({ dno: doc?.DocumentoNo, filename: doc.DocumentoNome, container_tag: this.cnfJson.project } as IFileDeleteRequest)
        .pipe(
          mergeMap(() => this.refresh(this.data, this.isEmpty)),
          error()
        );
    }

    return of(null);
  })

  /* It is called after the file be uploaded to the server and container. */
  afterFileUpload(doc: any): void {
    try {
      this.showList = true;
      this.subs.sink = this.refresh(this.data, this.isEmpty).subscribe(() => this.isLoading = false);

      if (this.isSingleFileType()) {
        this.dialogRef.close(doc);
      }

      if (this.fileUploadMode == EnFileUploadMode.single && doc?.Url && /\.(jpg|jpeg|png|gif)$/i.test(doc.Extensao)) {
        localStorage.setItem("imgUpload", doc.Url);
      }

      this.fileUploadMode = this.data?.enFileUploadMode;

      window.dispatchEvent(new Event('eventImgReturn'));

    } catch (error) {
      this.log.Registrar(this.constructor.name, 'afterFileUpload', error.message);
    }
  }

  private isSingleFileType(): boolean {
    try {
      return this.singleFileTypes.includes(this.data?.enFileUploadMode);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'isSingleFileType', error.message);
      return false;
    }
  }

  afterOpenWebcam($event: any): void {
    try {
      this.fileUploadMode = EnFileUploadMode.webcam;
      this.showList = $event;
    }
    catch (error) {
      this.log.Registrar(this.constructor.name, 'afterOpenWebcam', error.message);
    }
  }

  afterChooseFile($event: any): void {
    try {
      this.fileUploadMode = EnFileUploadMode.list;
      this.showList = $event;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'afterOpenWebcam', error.message);
    }
  }

  afterChooseFileSingle($event: any): void {
    try {
      this.fileUploadMode = EnFileUploadMode.single;
      this.showList = $event;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'afterOpenWebcam', error.message);
    }
  }

  onRenameClick(doc: IDocumento): void {
    try {
      this.fileName = doc.DocumentoNome;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onRenameClick', error.message);
    }
  }

  editFileName(doc: IDocumento): void {
    try {
      doc.DocumentoNomeExibicao = this.fileName;
      this.selectedDocument = doc;

      this.dataSource.data = this.dataSource.data.map((item: IDocumento) => {
        if (item.DocumentoNo === doc.DocumentoNo) {
          item.DocumentoNomeExibicao = this.fileName;
          item.Versao++;
        }
        return item;
      });

      this.editingId = null;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'editFileName', error.message);
    }
  }

  editFile($event: any, data: IFileUploadDialog): void {
    try {
      // this.isLoading = true;

      // const container_tag: string = `${this.cnfJson.project.toLowerCase()}`;
      // const newData = {
      //   dno: this.selectedDocument.DocumentoNo,
      //   ano: data.ano,
      //   ono: data.ono,
      //   vno: data.vno,
      //   filename: this.fileToUpload.name,
      //   newFilename: this.selectedDocument.DocumentoNome,
      //   container_tag,
      // };

      // this.subs.sink = this.editDocAndRefresh$(this.fileToUpload, newData).subscribe(() => this.isLoading = false);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'uploadFileToActivity', error.message);
    }
  }

  // private editDocAndRefresh$(file: File, data: IFileEditRequest) {
  //   return this.fileUploadSrv.editFile(file, data)
  //     .pipe(
  //       mergeMap(() => this.refresh(this.data, this.isEmpty)),
  //       error()
  //     )
  // }
}
